import React from 'react';
import { motion } from 'framer-motion';
import {MapPin, Train, DollarSign, Clock, Building2} from 'lucide-react';
import { useScrollAnimation, useParallax } from '../../hooks/useScrollAnimation';

export default function HongKong() {
  const scrollY = useParallax();
  const { ref: infoRef, isVisible: infoVisible } = useScrollAnimation(0.2);
  const { ref: areasRef, isVisible: areasVisible } = useScrollAnimation(0.2);
  const { ref: attractionsRef, isVisible: attractionsVisible } = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero with Parallax */}
      <div className="relative h-[500px] overflow-hidden">
        <motion.img
          src="https://images.pexels.com/photos/1525041/pexels-photo-1525041.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Hong Kong"
          className="w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 via-[#0A2540]/70 to-transparent" />
        <motion.div 
          className="absolute inset-0 flex items-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1 
              className="text-6xl font-bold text-white mb-3"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Hong Kong
            </motion.h1>
            <motion.p 
              className="text-2xl text-white/90"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Where Skyscrapers Meet Mountains & Tradition Meets Modernity
            </motion.p>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div 
          ref={infoRef}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={infoVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {[
            { icon: Clock, color: 'text-[#4A90E2]', label: 'Recommended Stay', value: '3-5 days' },
            { icon: DollarSign, color: 'text-[#F7B32B]', label: 'Daily Budget', value: 'HKD 500-1000' },
            { icon: Train, color: 'text-[#FF6B35]', label: 'Best Transport', value: 'MTR & Trams' },
            { icon: Building2, color: 'text-[#0A2540]', label: 'Visa', value: 'Visa-free for most' }
          ].map((item, idx) => (
            <motion.div 
              key={idx}
              className="bg-white rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              initial={{ opacity: 0, y: 20 }}
              animate={infoVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <item.icon className={`w-6 h-6 ${item.color} mb-2`} />
              <p className="text-sm text-gray-500">{item.label}</p>
              <p className="font-semibold text-gray-800">{item.value}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">About Hong Kong</h2>
            <p className="text-gray-600 mb-4">
              Hong Kong is a dazzling fusion of East and West where towering skyscrapers rise above traditional temples and bustling street markets. This former British colony offers world-class shopping, incredible food, stunning harbour views, and easy access to nature.
            </p>
            <p className="text-gray-600 mb-8">
              From the iconic Star Ferry to Victoria Peak views, from dim sum breakfasts to neon-lit night markets, Hong Kong packs an incredible diversity of experiences into a compact, efficient city.
            </p>

            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Must-Visit Areas</h3>
            <motion.div 
              ref={areasRef}
              className="space-y-4 mb-8"
            >
              {[
                { name: 'Central & Admiralty', desc: 'Financial hub, luxury shopping, Peak Tram' },
                { name: 'Tsim Sha Tsui', desc: 'Harbour views, museums, Avenue of Stars' },
                { name: 'Mong Kok', desc: 'Night markets, street food, local culture' },
                { name: 'Lantau Island', desc: 'Big Buddha, Ngong Ping, Disneyland' },
                { name: 'Sheung Wan', desc: 'Antique shops, hipster cafes, dried seafood markets' }
              ].map((area, idx) => (
                <motion.div 
                  key={idx} 
                  className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#FF6B35] hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, x: -30 }}
                  animate={areasVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ x: 10 }}
                >
                  <h4 className="font-semibold text-gray-800 mb-1">{area.name}</h4>
                  <p className="text-sm text-gray-600">{area.desc}</p>
                </motion.div>
              ))}
            </motion.div>

            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Top Attractions</h3>
            <motion.ul 
              ref={attractionsRef}
              className="space-y-2 text-gray-700"
            >
              {[
                { name: 'Victoria Peak', desc: 'Panoramic city and harbour views' },
                { name: 'Star Ferry', desc: 'Iconic harbour crossing (HKD 3.70!)' },
                { name: 'Tian Tan Buddha', desc: 'Giant bronze statue on Lantau Island' },
                { name: 'Temple Street Night Market', desc: 'Vibrant night shopping and street food' },
                { name: 'Hong Kong Disneyland', desc: 'Unique Asian Disney experience' }
              ].map((attraction, idx) => (
                <motion.li 
                  key={idx}
                  className="flex items-start hover:bg-white hover:shadow-sm p-2 rounded transition-all duration-200"
                  initial={{ opacity: 0, x: -20 }}
                  animate={attractionsVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                >
                  <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>{attraction.name}:</strong> {attraction.desc}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="bg-gradient-to-br from-[#F7B32B] to-[#FF6B35] rounded-xl p-6 text-white shadow-lg sticky top-4">
              <h3 className="text-2xl font-bold mb-4">Insider Tips</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">🎫</span>
                  <span>Get an Octopus card for seamless transport & shopping</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🥟</span>
                  <span>Try dim sum at Tim Ho Wan (Michelin-starred & affordable)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🌃</span>
                  <span>Watch the Symphony of Lights show at 8pm daily</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🏔️</span>
                  <span>Hike Dragon's Back for stunning coastal views</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🛍️</span>
                  <span>Shop at Ladies' Market in Mong Kok for bargains</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}